/**
 * graphConstants.ts
 *
 * Constants for the EC graph viewer, including model exclusions and enums.
 *
 * See also:
 * - @./graphDefaults.ts - Default values and settings for the graph viewer.
 * - @./components/GraphReactFlowContainer.tsx - Main graph viewer component.
 */

// #####################################################################
// TYPES OF EDGES AND GRAPHS:
// #####################################################################

export enum GraphNodeType {
  Table = "tableNode", // A node representing a table (model).
  EC = "ecNode", // A node representing an EC (endla component).
  UDF = "udfNode", // A node representing a UDF (User Defined Function).
  EDF = "edfNode", // A node representing an Endla Defined Function.
  DataSource = "dataSourceNode", // A node representing an external data source.
  Abstract = "abstractNode", // A node representing an abstract concept or grouping.
  DagAsset = "dagAssetNode", // A node representing a DagAsset workflow asset instance.
}

export enum GraphEdgeType {
  TableRelationship = "tableRelationshipEdge", // An edge connecting two tables (models) in a relationship.
  IO = "ioEdge", // An edge representing an input/output relationship.
  DagAsset = "dagAssetEdge", // An edge connecting two DagAsset nodes using their upstream dependencies.
  Abstract = "abstractEdge", // An edge representing abstract or conceptual relationships.
}

// #####################################################################
// RENDER SETTINGS:
// #####################################################################

// The maximum depth of graphs to traverse to find connections. If `GraphFilterSettings.depth` is set to a value higher than this we just show everything (even if not connected).
export const MAX_GRAPH_DEPTH = 8;

// Increase in edge stroke width when hovered or when connected to a hovered node.
export const HOVER_STROKE_WIDTH_INCREMENT = 2;

// #####################################################################
// ENUMS:
// #####################################################################

export enum ColorModes {
  ByLayer = "by-layer", // Nodes colors are based on the first layer they fall into (default).
  ByNodeType = "by-node-type", // Nodes colored based on their primary node type / layer definition.
  Unique = "unique", // Each node gets a color from a large set of `GRAPH_DEFAULT_COLOR_ARRAY` colors.
  GreyScale = "greyscale", // All greyscale, with Models slightly grey (`--color-muted-foreground`), everything else black (`--color-max-contrast`).
  Ghost = "ghost", // Barely visible.
}

export enum GraphLayoutType {
  LeftToRight = "left-to-right", // Arranges nodes horizontally from left to right regardless of tool mode.
  TopToBottom = "top-to-bottom", // Arranges nodes vertically from top to bottom regardless of tool mode.
  Waterfall = "waterfall", // Show the parent nodes first, then generally down to the right for child nodes. Maps to elk mode: '????'.

  Pyramid = "pyramid", // Show the selected nodes at the top, with children below. Maps to elk mode: '????'.
  Radial = "radial", // Show the selected nodes in the middle, and the others around them. Maps to elk mode: '????'.
}

export enum GraphToolMode {
  Default = "default", // Baseline mode where nodes behave with standard React Flow interactions.
  Select = "select", // Simple selection tool - you can select nodes/edges by clicking.
  Rectangle = "rectangle", // Drag out a rectangle to select multiple nodes/edges.
  Lasso = "lasso", // Draw a polygon to select multiple nodes/edges.
  Move = "move", // For moving nodes around.
  Pathfinder = "pathfinder", // Special mode for selecting paths between nodes.
  Edit = "edit", // Edit and add nodes/edges.
}

export enum GraphFilterDisplayMode {
  Hidden = "hidden", // Filters are fully hidden from table nodes.
  Preview = "preview", // Filters are shown in a read-only preview mode.
  Edit = "edit", // Filters are rendered with full editing capabilities.
}

export enum EdgeCardinalityDisplayMode {
  None = "none", // No cardinality markers are shown.
  CardinalityValues = "cardinality-values", // Numeric cardinality markers (1, n) are shown.
  CrowsFoot = "crows-foot", // Crow's Foot notation markers are shown.
}

export enum TopBarDisplayMode {
  Hidden = "hidden", // No floating panel is visible.
  Search = "search", // The search settings filter bar is visible.
  Legend = "legend", // The legend panel is visible.
}

/**
 * Represents how individual nodes should be rendered when layers or groups are toggled.
 */
export enum NodeVisibility {
  FullLockOn = "full-lock-on", // Always show, even if that layer/group is off.
  Default = "default", // Default visibility - shows when the correct layer & group is on... or if in `selectedNodesIds`
  Ghost = "ghost", // Same settings as default visibility, but only shows at 0.1 opacity.
  Hide = "hide", // Omitted from graph... even if matching layer/group is on.... only shows if in the node actively in `selectedNodeIds`.
}

// #####################################################################
// STRING UNION:
// #####################################################################

export type DetailLevel = "low" | "medium" | "high";
